import {
  collection,
  doc,
  increment,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import {
  getRatingVersionKey,
  isValidThumbValue,
} from "@/utils/ratingHelpers";
import {
  normalizeContentRatingFeedbackSubmission,
} from "@/utils/contentRatingFeedback.mjs";

const coerceNonNegativeCount = (value) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) {
    return Math.round(numeric);
  }
  return 0;
};

export const normalizeRatingPayload = (payload) => {
  if (typeof payload === "string") {
    return {
      value: payload,
      reasons: [],
      comment: "",
    };
  }

  if (!payload || typeof payload !== "object") {
    return {
      value: "",
      reasons: [],
      comment: "",
    };
  }

  return {
    value: payload.value,
    reasons: Array.isArray(payload.reasons) ? payload.reasons : [],
    comment: typeof payload.comment === "string" ? payload.comment : "",
  };
};

export const saveContentRating = async ({
  db,
  targetRef,
  contentType,
  contentId,
  contentVersion,
  payload,
  uid,
}) => {
  const normalizedPayload = normalizeRatingPayload(payload);
  const { value } = normalizedPayload;
  if (!isValidThumbValue(value)) {
    throw new Error("Invalid rating value.");
  }

  if (value === "up") {
    await updateDoc(targetRef, {
      "userRating.up": increment(1),
    });
    return { value, feedbackSaved: false };
  }

  const feedback = normalizeContentRatingFeedbackSubmission({
    contentType,
    reasons: normalizedPayload.reasons,
    comment: normalizedPayload.comment,
  });

  if (feedback.reasonKeys.length === 0) {
    throw new Error("At least one feedback reason is required.");
  }

  const contentVersionKey = getRatingVersionKey(contentVersion);
  const feedbackRef = doc(collection(targetRef, "ratingFeedback"));

  await runTransaction(db, async (transaction) => {
    const contentSnap = await transaction.get(targetRef);
    if (!contentSnap.exists()) {
      throw new Error("Rated content was not found.");
    }

    const currentRating = contentSnap.data()?.userRating || {};
    const currentVersionMatches =
      currentRating.currentVersionFeedbackVersion === contentVersionKey;
    const currentVersionFeedbackCount = currentVersionMatches
      ? coerceNonNegativeCount(currentRating.currentVersionFeedbackCount) + 1
      : 1;

    transaction.update(targetRef, {
      "userRating.down": coerceNonNegativeCount(currentRating.down) + 1,
      "userRating.feedbackCount":
        coerceNonNegativeCount(currentRating.feedbackCount) + 1,
      "userRating.currentVersionFeedbackCount": currentVersionFeedbackCount,
      "userRating.currentVersionFeedbackVersion": contentVersionKey,
    });

    transaction.set(feedbackRef, {
      contentType,
      contentId,
      contentVersion: contentVersionKey,
      reasonKeys: feedback.reasonKeys,
      reasonLabels: feedback.reasonLabels,
      comment: feedback.comment,
      uid: uid || "",
      createdAt: serverTimestamp(),
    });
  });

  return { value, feedbackSaved: true };
};
